import { periodicTable } from "./periodicTable";
import { nursingTermsData } from "./nursingTerms";
import { scienceTermsData } from "./scienceTerms";

/**
 * Generates an array of random letters (both uppercase and lowercase)
 * @param quantity - Number of letters to generate
 * @returns Array of random letters
 */
export const generateRandomLetters = (quantity: number): string[] => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const result: string[] = [];
  for (let i = 0; i < quantity; i++) {
    const randomLetter = letters.charAt(
      Math.floor(Math.random() * letters.length),
    );
    result.push(randomLetter);
  }
  return result;
};

/**
 * Generates an array of random uppercase letters, attempting to avoid duplicates
 * @param quantity - Number of letters to generate
 * @returns Array of random uppercase letters
 */
export const generateRandomUppercaseLetters = (quantity: number): string[] => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const uniqueLetters = Array.from(letters);
  const result: string[] = [];

  for (let i = 0; i < Math.min(quantity, uniqueLetters.length); i++) {
    const randomIndex = Math.floor(Math.random() * uniqueLetters.length);
    result.push(uniqueLetters[randomIndex]);
    uniqueLetters.splice(randomIndex, 1);
  }

  if (quantity > result.length) {
    const remainingCount = quantity - result.length;
    for (let i = 0; i < remainingCount; i++) {
      const randomIndex = Math.floor(Math.random() * letters.length);
      result.push(letters[randomIndex]);
    }
  }

  return result;
};

/**
 * Generates an array of random lowercase letters, attempting to avoid duplicates
 * @param quantity - Number of letters to generate
 * @returns Array of random lowercase letters
 */
export const generateRandomLowercaseLetters = (quantity: number): string[] => {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  const uniqueLetters = Array.from(letters);
  const result: string[] = [];

  for (let i = 0; i < Math.min(quantity, uniqueLetters.length); i++) {
    const randomIndex = Math.floor(Math.random() * uniqueLetters.length);
    result.push(uniqueLetters[randomIndex]);
    uniqueLetters.splice(randomIndex, 1);
  }

  if (quantity > result.length) {
    const remainingCount = quantity - result.length;
    for (let i = 0; i < remainingCount; i++) {
      const randomIndex = Math.floor(Math.random() * letters.length);
      result.push(letters[randomIndex]);
    }
  }

  return result;
};

/**
 * Generates an array of random mixed case letters, attempting to avoid duplicates
 * @param quantity - Number of letters to generate
 * @returns Array of random mixed case letters
 */
export const generateRandomMixedCaseLetters = (quantity: number): string[] => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const uniqueLetters = Array.from(letters);
  const result: string[] = [];

  for (let i = 0; i < Math.min(quantity, uniqueLetters.length); i++) {
    const randomIndex = Math.floor(Math.random() * uniqueLetters.length);
    result.push(uniqueLetters[randomIndex]);
    uniqueLetters.splice(randomIndex, 1);
  }

  if (quantity > result.length) {
    const remainingCount = quantity - result.length;
    for (let i = 0; i < remainingCount; i++) {
      const randomIndex = Math.floor(Math.random() * letters.length);
      result.push(letters[randomIndex]);
    }
  }

  return result;
};

/**
 * Generates an array of random numbers as strings
 * @param quantity - Number of numbers to generate
 * @returns Array of random numbers as strings
 */
export const generateRandomNumbers = (quantity: number): string[] => {
  const uniqueNumbers = Array.from({ length: 100 }, (_, i) =>
    (i + 1).toString(),
  );
  const result: string[] = [];

  for (let i = 0; i < Math.min(quantity, uniqueNumbers.length); i++) {
    const randomIndex = Math.floor(Math.random() * uniqueNumbers.length);
    result.push(uniqueNumbers[randomIndex]);
    uniqueNumbers.splice(randomIndex, 1);
  }

  if (quantity > result.length) {
    const remainingCount = quantity - result.length;
    for (let i = 0; i < remainingCount; i++) {
      const randomIndex = Math.floor(Math.random() * 100) + 1; // Random numbers from 1 to 100
      result.push(randomIndex.toString());
    }
  }

  return result;
};

/**
 * Generates the full alphabet in both uppercase and lowercase
 * @returns Array containing all letters A-Z and a-z
 */
export const generateFullAlphabet = (): string[] => {
  const uppercase = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(65 + i),
  ); // A-Z
  const lowercase = Array.from({ length: 26 }, (_, i) =>
    String.fromCharCode(97 + i),
  ); // a-z
  return [...uppercase, ...lowercase];
};

/**
 * Generates numbers from 1 to 100 as strings
 * @returns Array of numbers 1-100 as strings
 */
export const generateNumbersOneToHundred = (): string[] => {
  return Array.from({ length: 100 }, (_, i) => (i + 1).toString());
};

/**
 * Generates numbers from 1 to 20 as strings
 * @returns Array of numbers 1-20 as strings
 */
export const generateNumbersOneToTwenty = (): string[] => {
  return Array.from({ length: 20 }, (_, i) => (i + 1).toString());
};

/** Mapping of operation types to their display symbols */
const operationSymbol = {
  multiplication: "×",
  addition: "+",
  subtraction: "−",
  division: "÷",
} as const;

/**
 * Generates random math problems with solutions
 * @param count - Number of problems to generate
 * @param operation - Type of mathematical operation
 * @returns Array of problems with their answers
 */
export const generateMathProblems = (
  count: number,
  operation: keyof typeof operationSymbol,
): { problem: string; answer: number }[] => {
  const problems: { problem: string; answer: number }[] = [];
  for (let i = 0; i < count; i++) {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    let answer: number;
    switch (operation) {
      case "addition":
        answer = num1 + num2;
        break;
      case "subtraction":
        answer = num1 - num2;
        break;
      case "multiplication":
        answer = num1 * num2;
        break;
      case "division":
        answer = Math.round((num1 / num2) * 100) / 100;
        break;
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
    problems.push({
      problem: `${num1} ${operationSymbol[operation]} ${num2}`,
      answer: answer,
    });
  }
  return problems;
};

export const NUMBER_SENSE_COLORS = [
  "blue",
  "green",
  "red",
  "purple",
  "orange",
] as const;

export const NUMBER_SENSE_SHAPES = ["circle", "square", "triangle"] as const;

export type NumberSenseColor = (typeof NUMBER_SENSE_COLORS)[number];
export type NumberSenseShape = (typeof NUMBER_SENSE_SHAPES)[number];

export const pickRandomFrom = <T,>(items: readonly T[]): T => {
  return items[Math.floor(Math.random() * items.length)];
};

export type NumberSenseMixMode = "never" | "sometimes" | "always";

export type NumberSenseDot = {
  color: string;
  shape: string;
};

const SHAPE_PLURALS: Record<string, string> = {
  circle: "circles",
  square: "squares",
  triangle: "triangles",
};

export const isNumberSenseQuestion = (name?: string): boolean => {
  return Boolean(name && name.toLowerCase().startsWith("how many"));
};

const numberSenseShapeMarkup = (
  x: number,
  y: number,
  cellSize: number,
  color: string,
  shape: string,
): string => {
  switch (shape) {
    case "square":
      return `<rect x="${x - cellSize / 3}" y="${y - cellSize / 3}" width="${(cellSize * 2) / 3}" height="${(cellSize * 2) / 3}" fill="${color}" />`;
    case "triangle": {
      const size = (cellSize * 2) / 3;
      return `<polygon points="${x},${y - size / 2} ${x - size / 2},${y + size / 2} ${x + size / 2},${y + size / 2}" fill="${color}" />`;
    }
    default:
      return `<circle cx="${x}" cy="${y}" r="${cellSize / 3}" fill="${color}" />`;
  }
};

const pickNumberSenseQuestion = (
  dots: NumberSenseDot[],
): { name: string; count: number } => {
  const total = dots.length;
  const colorCounts = new Map<string, number>();
  const shapeCounts = new Map<string, number>();
  for (const dot of dots) {
    colorCounts.set(dot.color, (colorCounts.get(dot.color) || 0) + 1);
    shapeCounts.set(dot.shape, (shapeCounts.get(dot.shape) || 0) + 1);
  }

  const subsetOptions: { name: string; count: number }[] = [];
  if (colorCounts.size > 1) {
    for (const [color, count] of colorCounts) {
      subsetOptions.push({ name: `How many are ${color}?`, count });
    }
  }
  if (shapeCounts.size > 1) {
    for (const [shape, count] of shapeCounts) {
      const label = SHAPE_PLURALS[shape] ?? `${shape}s`;
      subsetOptions.push({ name: `How many ${label}?`, count });
    }
  }

  if (subsetOptions.length === 0) {
    return { name: "How many?", count: total };
  }

  const properSubsets = subsetOptions.filter((option) => option.count < total);
  return pickRandomFrom(
    properSubsets.length > 0 ? properSubsets : subsetOptions,
  );
};

const forceMixedDots = (
  dots: NumberSenseDot[],
  colors: readonly string[],
  shapes: readonly string[],
): NumberSenseDot[] => {
  if (dots.length < 2) {
    return dots;
  }
  const next = dots.map((dot) => ({ ...dot }));
  if (colors.length > 1 && new Set(next.map((dot) => dot.color)).size === 1) {
    const other = colors.find((color) => color !== next[0].color) ?? colors[0];
    next[0] = { ...next[0], color: other };
  }
  if (shapes.length > 1 && new Set(next.map((dot) => dot.shape)).size === 1) {
    const last = next.length - 1;
    const other =
      shapes.find((shape) => shape !== next[last].shape) ?? shapes[0];
    next[last] = { ...next[last], shape: other };
  }
  return next;
};

export const shouldMixNumberSenseCard = (
  mixMode: NumberSenseMixMode,
  count: number,
  colors: readonly string[],
  shapes: readonly string[],
): boolean => {
  const canMix = count >= 2 && (colors.length > 1 || shapes.length > 1);
  if (!canMix) {
    return false;
  }
  if (mixMode === "always") {
    return true;
  }
  if (mixMode === "sometimes") {
    return Math.random() < 0.5;
  }
  return false;
};

export const buildNumberSenseDotCounts = (
  cardCount: number,
  minDots: number,
  maxDots: number,
): number[] => {
  const count = Math.max(1, cardCount);
  const low = Math.max(1, Math.min(minDots, maxDots));
  const high = Math.max(low, Math.max(minDots, maxDots));
  const range: number[] = [];
  for (let value = low; value <= high; value += 1) {
    range.push(value);
  }

  const counts: number[] = [];
  while (counts.length < count) {
    const bag = [...range];
    for (let index = bag.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(Math.random() * (index + 1));
      [bag[index], bag[swap]] = [bag[swap], bag[index]];
    }
    for (const value of bag) {
      if (counts.length >= count) {
        break;
      }
      counts.push(value);
    }
  }
  return counts;
};

export function generateNumberSenseCard(
  count: number,
  colors: readonly string[],
  shapes: readonly string[],
  mixWithinCard: boolean,
): { svg: string; count: number; name: string } {
  const colorPool = colors.length > 0 ? colors : ["blue"];
  const shapePool = shapes.length > 0 ? shapes : ["circle"];
  const mix =
    mixWithinCard &&
    count >= 2 &&
    (colorPool.length > 1 || shapePool.length > 1);

  let dots: NumberSenseDot[];
  if (!mix) {
    const color = pickRandomFrom(colorPool);
    const shape = pickRandomFrom(shapePool);
    dots = Array.from({ length: count }, () => ({ color, shape }));
  } else {
    dots = Array.from({ length: count }, () => ({
      color: pickRandomFrom(colorPool),
      shape: pickRandomFrom(shapePool),
    }));
    dots = forceMixedDots(dots, colorPool, shapePool);
  }

  const gridSize = 5;
  const cellSize = 40;
  const svgSize = gridSize * cellSize;
  const positions = getRandomPositions(count, gridSize);
  const markup = positions
    .map((pos, index) => {
      const dot = dots[index];
      const x = pos.x * cellSize + cellSize / 2;
      const y = pos.y * cellSize + cellSize / 2;
      return numberSenseShapeMarkup(x, y, cellSize, dot.color, dot.shape);
    })
    .join("");
  const svgContent = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
      ${markup}
    </svg>
  `;
  const question = pickNumberSenseQuestion(dots);
  return {
    svg: `data:image/svg+xml,${encodeURIComponent(svgContent)}`,
    count: question.count,
    name: question.name,
  };
}

/**
 * Generates SVG images for number sense exercises
 * @param count - Number of shapes to generate
 * @param color - Color of the shapes
 * @param shape - Type of shape to generate
 * @returns Object containing SVG data and count
 */
export function generateNumberSenseImages(
  count: number,
  color: string = "blue",
  shape: string = "circle",
): { url: string; svg: string; count: number } {
  const svg = generateCountingSvg(count, color, shape);
  return {
    url: svg,
    svg: svg,
    count: count,
  };
}

export function generateCountingSvg(
  count: number,
  color: string = "blue",
  shape: string = "circle",
  position: string = "random",
): string {
  const gridSize = 5; // 5x5 grid
  const cellSize = 40;
  const svgSize = gridSize * cellSize;

  let positions: { x: number; y: number }[];
  if (position === "random") {
    positions = getRandomPositions(count, gridSize);
  } else {
    positions = getPositionsByPreference(count, gridSize, position);
  }

  const svgContent = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
      ${positions
        .map((pos) => {
          const x = pos.x * cellSize + cellSize / 2;
          const y = pos.y * cellSize + cellSize / 2;
          let shapeElement = "";
          switch (shape) {
            case "circle":
              shapeElement = `<circle cx="${x}" cy="${y}" r="${cellSize / 3}" fill="${color}" />`;
              break;
            case "square":
              shapeElement = `<rect x="${x - cellSize / 3}" y="${y - cellSize / 3}" width="${(cellSize * 2) / 3}" height="${(cellSize * 2) / 3}" fill="${color}" />`;
              break;
            case "triangle": {
              const size = (cellSize * 2) / 3;
              shapeElement = `<polygon points="${x},${y - size / 2} ${x - size / 2},${y + size / 2} ${x + size / 2},${y + size / 2}" fill="${color}" />`;
              break;
            }
          }
          return shapeElement;
        })
        .join("")}
    </svg>
  `;
  return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
}

export function generateGridNumberSenseSvg(
  dots: Array<{ position: string; color: string; shape: string }>,
): string {
  const gridSize = 5;
  const cellSize = 40;
  const svgSize = gridSize * cellSize;

  const shapes = dots.map((dot) => {
    const position = Math.max(1, Math.min(25, parseInt(dot.position, 10) || 1));
    const xIndex = (position - 1) % gridSize;
    const yIndex = Math.floor((position - 1) / gridSize);
    const x = xIndex * cellSize + cellSize / 2;
    const y = yIndex * cellSize + cellSize / 2;
    const color = dot.color || "blue";

    switch (dot.shape) {
      case "square":
        return `<rect x="${x - cellSize / 3}" y="${y - cellSize / 3}" width="${(cellSize * 2) / 3}" height="${(cellSize * 2) / 3}" fill="${color}" />`;
      case "triangle": {
        const size = (cellSize * 2) / 3;
        return `<polygon points="${x},${y - size / 2} ${x - size / 2},${y + size / 2} ${x + size / 2},${y + size / 2}" fill="${color}" />`;
      }
      default:
        return `<circle cx="${x}" cy="${y}" r="${cellSize / 3}" fill="${color}" />`;
    }
  });

  const svgContent = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${svgSize}" height="${svgSize}" viewBox="0 0 ${svgSize} ${svgSize}">
      ${shapes.join("")}
    </svg>
  `;
  return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
}

/**
 * Generates random positions on a grid based on specified pattern
 * @param count - Number of positions to generate
 * @param gridSize - Size of the grid (width/height)
 * @param pattern - Pattern to follow ('random', 'diagonal', 'horizontal', 'vertical')
 * @returns Array of position objects with x,y coordinates
 */
export function getRandomPositions(
  count: number,
  gridSize: number,
): { x: number; y: number }[] {
  const positions: { x: number; y: number }[] = [];
  const allPositions = Array.from({ length: gridSize * gridSize }, (_, i) => ({
    x: i % gridSize,
    y: Math.floor(i / gridSize),
  }));

  for (let i = 0; i < count; i++) {
    if (allPositions.length === 0) break;
    const index = Math.floor(Math.random() * allPositions.length);
    positions.push(allPositions[index]);
    allPositions.splice(index, 1);
  }

  return positions;
}

function getPositionsByPreference(
  count: number,
  gridSize: number,
  preference: string,
): { x: number; y: number }[] {
  const positions: { x: number; y: number }[] = [];
  let startX: number, startY: number;

  switch (preference) {
    case "1": // Top Left
      startX = 0;
      startY = 0;
      break;
    case "2": // Top Right
      startX = gridSize - 1;
      startY = 0;
      break;
    case "3": // Center
      startX = Math.floor(gridSize / 2);
      startY = Math.floor(gridSize / 2);
      break;
    case "4": // Bottom Left
      startX = 0;
      startY = gridSize - 1;
      break;
    case "5": // Bottom Right
      startX = gridSize - 1;
      startY = gridSize - 1;
      break;
    default:
      return getRandomPositions(count, gridSize);
  }

  for (let i = 0; i < count; i++) {
    positions.push({ x: startX, y: startY });
    if (startX < gridSize - 1) {
      startX++;
    } else {
      startX = 0;
      startY = (startY + 1) % gridSize;
    }
  }

  return positions;
}

/**
 * Generates an array of periodic table elements with their details
 * @param count - Number of elements to generate
 * @returns Array of strings containing element symbol, name, and atomic number
 */
export const generatePeriodicTableElements = (count: number): string[] => {
  const elements = Object.values(periodicTable);
  const shuffled = elements.sort(() => 0.5 - Math.random());
  return shuffled
    .slice(0, count)
    .map((element) => `${element.symbol} - ${element.name} - ${element.atomicNumber}`);
};

/**
 * Generates an array of random science terms
 * Repeats terms if count exceeds available unique terms
 * @param count - Number of terms to generate
 * @returns Array of science terms
 */
export const generateScienceTerms = (count: number): string[] => {
  const allTerms = Object.values(scienceTermsData)
    .flat()
    .filter((term): term is string => typeof term === "string");

  if (count > allTerms.length) {
    const repeatedTerms = [];
    while (repeatedTerms.length < count) {
      repeatedTerms.push(...shuffleArray([...allTerms]));
    }
    return repeatedTerms.slice(0, count);
  }

  return shuffleArray(allTerms).slice(0, count);
};

/**
 * Generates an array of random nursing terms
 * Repeats terms if count exceeds available unique terms
 * @param count - Number of terms to generate
 * @returns Array of nursing terms
 */
export const generateNursingTerms = (count: number): string[] => {
  const allTerms = Object.values(nursingTermsData)
    .flat()
    .filter((term): term is string => typeof term === "string");

  if (count > allTerms.length) {
    const repeatedTerms = [];
    while (repeatedTerms.length < count) {
      repeatedTerms.push(...shuffleArray([...allTerms]));
    }
    return repeatedTerms.slice(0, count);
  }

  return shuffleArray(allTerms).slice(0, count);
};

/**
 * Generates a complete list of all periodic table elements
 * @returns Array of strings containing element details
 */
export const generateFullPeriodicTable = (): string[] => {
  return Object.values(periodicTable).map(
    (element) => `${element.symbol} - ${element.name} - ${element.atomicNumber}`
  );
};

/**
 * Returns all science terms in random order
 * @returns Array of all available science terms
 */
export const getAllScienceTerms = (): string[] => {
  const allTerms = Object.values(scienceTermsData)
    .flat()
    .filter((term): term is string => typeof term === "string");
  return shuffleArray(allTerms);
};

/**
 * Returns all nursing terms in random order
 * @returns Array of all available nursing terms
 */
export const getAllNursingTerms = (): string[] => {
  const allTerms = Object.values(nursingTermsData)
    .flat()
    .filter((term): term is string => typeof term === "string");
  return shuffleArray(allTerms);
};

// Initialize the alertShown property if it doesn't exist
if (typeof window.alertShown === "undefined") {
  window.alertShown = false;
}

/**
 * Helper function to shuffle an array using Fisher-Yates algorithm
 * @param array - Array to shuffle
 * @returns Shuffled array
 */
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Generates an SVG representation of a domino with specified dots on left and right sides
 * @param leftCount - Number of dots on the left side
 * @param rightCount - Number of dots on the right side
 * @param dotColor - Color of the dots
 * @returns SVG string representation of the domino
 */
export const generateDominoSvg = (
  leftCount: number,
  rightCount: number,
  dotColor: string = "black"
): string => {
  // Domino dimensions
  const width = 200;
  const height = 100;
  const radius = 8; // Dot radius
  const dividerWidth = 4;
  
  // SVG header
  let svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">`;
  
  // Domino background
  svg += `<rect x="0" y="0" width="${width}" height="${height}" rx="10" ry="10" fill="white" stroke="black" stroke-width="2" />`;
  
  // Divider line
  svg += `<line x1="${width/2}" y1="0" x2="${width/2}" y2="${height}" stroke="black" stroke-width="${dividerWidth}" />`;
  
  // Generate positions for left side dots
  const leftPositions = getDotPositions(leftCount, width/2 - dividerWidth/2, height, radius);
  
  // Generate positions for right side dots
  const rightPositions = getDotPositions(rightCount, width/2 + dividerWidth/2, height, radius, width/2);
  
  // Draw all dots
  [...leftPositions, ...rightPositions].forEach(pos => {
    svg += `<circle cx="${pos.x}" cy="${pos.y}" r="${radius}" fill="${dotColor}" />`;
  });
  
  // Close SVG
  svg += '</svg>';
  
  return svg;
};

/**
 * Helper function to calculate dot positions for a domino side
 * @param count - Number of dots to position
 * @param areaWidth - Width of the area
 * @param areaHeight - Height of the area
 * @param radius - Radius of each dot
 * @param offsetX - X offset for positioning (default 0)
 * @returns Array of {x, y} coordinates for dots
 */
function getDotPositions(
  count: number, 
  areaWidth: number, 
  areaHeight: number, 
  radius: number,
  offsetX: number = 0
): {x: number, y: number}[] {
  const positions: {x: number, y: number}[] = [];
  const padding = radius * 2;
  
  // Standard positions for domino dots (1-6)
  if (count === 1) {
    // Center
    positions.push({
      x: offsetX + areaWidth/2,
      y: areaHeight/2
    });
  } else if (count === 2) {
    // Top-left and bottom-right
    positions.push(
      {x: offsetX + padding, y: padding},
      {x: offsetX + areaWidth - padding, y: areaHeight - padding}
    );
  } else if (count === 3) {
    // Top-left, center, and bottom-right
    positions.push(
      {x: offsetX + padding, y: padding},
      {x: offsetX + areaWidth/2, y: areaHeight/2},
      {x: offsetX + areaWidth - padding, y: areaHeight - padding}
    );
  } else if (count === 4) {
    // Four corners
    positions.push(
      {x: offsetX + padding, y: padding},
      {x: offsetX + areaWidth - padding, y: padding},
      {x: offsetX + padding, y: areaHeight - padding},
      {x: offsetX + areaWidth - padding, y: areaHeight - padding}
    );
  } else if (count === 5) {
    // Four corners plus center
    positions.push(
      {x: offsetX + padding, y: padding},
      {x: offsetX + areaWidth - padding, y: padding},
      {x: offsetX + areaWidth/2, y: areaHeight/2},
      {x: offsetX + padding, y: areaHeight - padding},
      {x: offsetX + areaWidth - padding, y: areaHeight - padding}
    );
  } else if (count === 6) {
    // Two rows of three
    positions.push(
      {x: offsetX + padding, y: padding},
      {x: offsetX + areaWidth - padding, y: padding},
      {x: offsetX + padding, y: areaHeight/2},
      {x: offsetX + areaWidth - padding, y: areaHeight/2},
      {x: offsetX + padding, y: areaHeight - padding},
      {x: offsetX + areaWidth - padding, y: areaHeight - padding}
    );
  } else {
    // For other counts, distribute dots evenly
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / 3);
      const col = i % 3;
      positions.push({
        x: offsetX + padding + col * ((areaWidth - 2 * padding) / 2),
        y: padding + row * ((areaHeight - 2 * padding) / 2)
      });
    }
  }
  
  return positions;
}

/**
 * Generates a collection of domino comparison items
 * @param count - Number of domino items to generate
 * @param minDots - Minimum number of dots per side
 * @param maxDots - Maximum number of dots per side
 * @param dotColor - Color of the dots
 * @returns Array of objects with domino SVGs and correct answers
 */
export const generateDominoComparisons = (
  count: number,
  minDots: number = 1,
  maxDots: number = 6,
  dotColor: string = "black"
): Array<{name: string, svg: string, answer: string}> => {
  const items = [];
  
  for (let i = 0; i < count; i++) {
    // Generate random dot counts for left and right sides
    const leftCount = Math.floor(Math.random() * (maxDots - minDots + 1)) + minDots;
    const rightCount = Math.floor(Math.random() * (maxDots - minDots + 1)) + minDots;
    
    // Determine which side has more dots (or if they're equal)
    let answer: string;
    if (leftCount > rightCount) {
      answer = "left";
    } else if (rightCount > leftCount) {
      answer = "right";
    } else {
      answer = "equal";
    }
    
    // Generate the domino SVG
    const svg = generateDominoSvg(leftCount, rightCount, dotColor);
    
    // Create the item
    items.push({
      name: `Domino: ${leftCount} vs ${rightCount}`,
      svg,
      answer
    });
  }
  
  return items;
};
